import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send, Loader2, Bot, ChevronDown, CheckCircle2, ExternalLink, ListChecks, Moon } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api } from "../lib/api";

export const OrgTelegramAlerts = () => {
  const [cfg, setCfg] = useState({ enabled: false, chat_id: "", bot_set: false, bot_username: "", daily_digest: false, evening_report: false, webhook_set: false });
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const [chats, setChats] = useState([]);
  const [busy, setBusy] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try {
      const d = await api.getOrgTelegram();
      setCfg(d);
      // Ensure the bot webhook is registered so /myid self-service works for existing bots.
      if (d.bot_set && !d.webhook_set && BACKEND_URL) {
        try { setCfg(await api.saveOrgTelegram({ base_url: BACKEND_URL })); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }, [BACKEND_URL]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, label) => {
    setBusy(label);
    try { const d = await api.saveOrgTelegram(body); setCfg(d); return d; }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); throw e; }
    finally { setBusy(""); }
  };

  const saveToken = async () => {
    if (!token.trim()) { toast.warning("ألصق توكن البوت أولاً"); return; }
    await patch({ bot_token: token.trim(), base_url: BACKEND_URL }, "token");
    setToken("");
    toast.success("تم حفظ التوكن");
  };

  const discover = async () => {
    setBusy("discover");
    try { const d = await api.discoverOrgTelegram(); setChats(d.chats || []); toast.success("تم جلب المحادثات — اختر محادثتك"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الجلب"); }
    finally { setBusy(""); }
  };

  const pickChat = async (id) => { await patch({ chat_id: id }, "chat"); toast.success("تم ربط المحادثة"); };

  const sendEvening = async () => {
    setBusy("evening");
    try { await api.testOrgEvening(); toast.success("أُرسل تقرير المساء إلى تيليجرام ✅"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test");
    try { await api.testOrgTelegram(); toast.success("أُرسلت رسالة تجربة إلى تيليجرام ✅"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(""); }
  };

  const sendSummary = async () => {
    setBusy("summary");
    try {
      const d = await api.sendOrgTelegramSummary();
      toast.success(d.count ? `أُرسل ملخص بـ ${d.count} وثيقة إلى تيليجرام ✅` : "لا توجد وثائق منتهية أو قريبة — أُرسل تأكيد ✅");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(""); }
  };

  const sendDigest = async () => {
    setBusy("digest");
    try { await api.testOrgDailyDigest(); toast.success("أُرسل الملخص اليومي إلى تيليجرام ✅"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); }
    finally { setBusy(""); }
  };

  const ready = cfg.bot_set && !!cfg.chat_id;

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/60 overflow-hidden sm:col-span-2" data-testid="org-telegram-alerts">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-right hover:bg-sky-100/50">
        <span className="flex items-center gap-2 text-sm font-bold text-sky-900">
          <Bot className="h-5 w-5 text-sky-600" /> تنبيهات انتهاء الوثائق على تيليجرام
          <span className={`text-[10px] rounded-full px-2 py-0.5 ${cfg.enabled && ready ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
            {cfg.enabled && ready ? "مُفعّل" : "غير مُفعّل"}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 text-sky-500 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-sky-100 p-3 space-y-4 bg-white/70">
          <ol className="text-[11px] text-slate-500 leading-relaxed list-decimal pr-4 space-y-0.5">
            <li>أنشئ بوتاً عبر <b>@BotFather</b> في تيليجرام بإرسال <code>/newbot</code> واحصل على التوكن.</li>
            <li>ألصق التوكن بالأسفل واحفظه.</li>
            <li>أرسل <code>/start</code> لبوتك من جوالك، ثم اضغط «اكتشف المحادثات» واختر محادثتك.</li>
            <li>فعّل التنبيهات. تُرسل رسالة لكل وثيقة تقارب على الانتهاء عند كل فحص.</li>
          </ol>

          {cfg.bot_username && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800 leading-relaxed" data-testid="org-tg-myid-hint">
              💡 <b>للمشرفين:</b> ليحصل كل مشرف على <b>Chat ID</b> الخاص به، يفتح بوت الشركة
              {" "}<a href={`https://t.me/${cfg.bot_username}`} target="_blank" rel="noreferrer" className="text-emerald-700 underline">@{cfg.bot_username}</a>{" "}
              ويرسل <code>/myid</code> — سيرد عليه البوت برقمه مباشرةً لينسخه في ملفه.
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">توكن البوت (Bot Token)
              {cfg.bot_set && <span className="text-emerald-600 mr-1 inline-flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> محفوظ</span>}
            </Label>
            <div className="flex gap-2">
              <Input data-testid="org-tg-token-input" dir="ltr" type="password" value={token} onChange={(e) => setToken(e.target.value)}
                placeholder={cfg.bot_set ? "••••••••  (أدخل توكن جديد للتبديل)" : "123456:ABC-DEF..."} className="flex-1 text-xs" />
              <Button data-testid="org-tg-save-token" onClick={saveToken} disabled={busy === "token"} size="sm" className="bg-sky-600 hover:bg-sky-700 shrink-0">
                {busy === "token" ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">محادثة الاستقبال</Label>
            {cfg.bot_username && (
              <a href={`https://t.me/${cfg.bot_username}`} target="_blank" rel="noreferrer" data-testid="org-tg-open-bot"
                className="flex items-center gap-1.5 text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5 w-fit hover:bg-sky-100">
                <ExternalLink className="h-3.5 w-3.5" /> افتح بوتك @{cfg.bot_username} وأرسل <code>/start</code>
              </a>
            )}
            <div className="flex gap-2 items-center">
              <Button data-testid="org-tg-discover" onClick={discover} disabled={busy === "discover" || !cfg.bot_set} size="sm" variant="outline" className="shrink-0 gap-1.5">
                {busy === "discover" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} اكتشف المحادثات
              </Button>
              <Select value={cfg.chat_id || ""} onValueChange={pickChat}>
                <SelectTrigger data-testid="org-tg-chat-select" className="flex-1 bg-white text-xs"><SelectValue placeholder="اختر محادثتك" /></SelectTrigger>
                <SelectContent>
                  {chats.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} <span dir="ltr" className="text-slate-400">({c.id})</span></SelectItem>))}
                  {cfg.chat_id && !chats.some((c) => c.id === cfg.chat_id) && (<SelectItem value={cfg.chat_id}>المحادثة المربوطة ({cfg.chat_id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">تفعيل تنبيهات تيليجرام</p>
              <p className="text-[11px] text-slate-400">تعمل بشكل مستقل عن الواتساب والإيميل</p>
            </div>
            <Switch data-testid="org-tg-enabled-switch" checked={cfg.enabled} disabled={!ready}
              className="data-[state=checked]:bg-blue-600"
              onCheckedChange={(v) => patch({ enabled: v }, "enabled")} />
          </div>

          <Button data-testid="org-tg-test" onClick={sendTest} disabled={busy === "test" || !ready} variant="outline" className="w-full gap-1.5">
            {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال رسالة تجربة
          </Button>
          <Button data-testid="org-tg-summary" onClick={sendSummary} disabled={busy === "summary" || !ready}
            className="w-full gap-1.5 bg-sky-600 hover:bg-sky-700">
            {busy === "summary" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} إرسال ملخص الآن
          </Button>

          <div data-testid="org-tg-interactive-info" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] leading-relaxed text-sky-900">
            <p className="font-semibold">🤖 البوت تفاعلي (براء)</p>
            <p>اكتب في المحادثة المربوطة أي سؤال مثل «كم الطلبات أمس؟» أو استخدم الأزرار: الطلبات، ملخص اليوم، المستندات المنتهية، كروت التشغيل، الوقود، مقارنة اليوم بأمس.</p>
            <p className="text-sky-700">المدراء والمشرفون: من «الأدوات ← ربط حسابي بتلقرام» ليسألوا البوت في الخاص ويروا فروعهم فقط. في القروبات استخدم الأوامر مثل /summary أو /ask سؤالك.</p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">الملخص اليومي التلقائي</p>
              <p className="text-[11px] text-slate-400">يصلك كل يوم 5:00 صباحاً بكل التقارير المهمة</p>
            </div>
            <Switch data-testid="org-tg-digest-switch" checked={cfg.daily_digest} disabled={!ready}
              className="data-[state=checked]:bg-blue-600"
              onCheckedChange={(v) => patch({ daily_digest: v }, "digest-toggle")} />
          </div>
          <Button data-testid="org-tg-digest-now" onClick={sendDigest} disabled={busy === "digest" || !ready} variant="outline" className="w-full gap-1.5">
            {busy === "digest" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />} إرسال ملخص اليوم الآن (تجربة)
          </Button>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">تقرير المساء (9:00 مساءً)</p>
              <p className="text-[11px] text-slate-400">طلبات اليوم مقارنة بأمس + أفضل وأضعف المناديب</p>
            </div>
            <Switch data-testid="org-tg-evening-switch" checked={cfg.evening_report} disabled={!ready}
              className="data-[state=checked]:bg-blue-600"
              onCheckedChange={(v) => patch({ evening_report: v }, "evening-toggle")} />
          </div>
          <Button data-testid="org-tg-evening-now" onClick={sendEvening} disabled={busy === "evening" || !ready} variant="outline" className="w-full gap-1.5">
            {busy === "evening" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Moon className="h-4 w-4" />} إرسال تقرير المساء الآن (تجربة)
          </Button>
        </div>
      )}
    </div>
  );
};

export default OrgTelegramAlerts;
